import Image from 'next/image';
import React from 'react';

import cameraImg from '@ui/assets/images/camera.png';
import chessImg from '@ui/assets/images/chess.png';
import cofeeImg from '@ui/assets/images/cofee.png';
import { Badge } from '@ui/components/common/badge';
import SvgGrowth from '@ui/components/icons/Growth';
import SvgHealthAndWelness from '@ui/components/icons/HealthAndWelness';
import SvgMusicDance from '@ui/components/icons/MusicDance';
import { cn } from '@ui/lib/utils';

export interface VisualSectionProps {
  musicDancesLabel?: string;
  personalDevelopmentLabel?: string;
  healthWellnessLabel?: string;
  /** Number of photos to display (2 or 3). When 2, the coffee image and Health & Wellness badge are hidden and the remaining images are larger. */
  photoCount?: 2 | 3;
  className?: string;
}

export const VisualSection: React.FC<VisualSectionProps> = ({
  musicDancesLabel = 'Music & Dances',
  personalDevelopmentLabel = 'Personal Development',
  healthWellnessLabel = 'Health & Wellness',
  photoCount = 2,
  className = ''
}) => {
  const showThirdPhoto = photoCount === 3;
  return (
    <div className={cn('flex flex-1 w-full items-center justify-center md:w-1/2 md:justify-end', className)}>
      <div
        className={cn(
          'relative w-full overflow-visible',
          showThirdPhoto ? 'max-w-[430px] h-[300px] sm:h-[350px]' : 'max-w-[500px] h-[340px] sm:h-[400px]'
        )}
      >
        {/* Background shapes */}
        <div
          className="absolute right-[5%] top-[6%] h-8 w-8 sm:h-12 sm:w-10 rounded-sm bg-gradient-to-b from-[#E2F4FC] to-[#CBEFFF] opacity-70"
          aria-hidden="true"
        />
        <div
          className="absolute right-[15%] top-[-3%] h-7 w-5 sm:h-9 sm:w-6 rounded-sm bg-gradient-to-b from-[#D9D6FE] to-[#D6D3FF] opacity-70"
          aria-hidden="true"
        />
        <div
          className="absolute left-[20%] bottom-[15%] h-9 w-12 sm:h-11 sm:w-14 rounded-sm bg-gradient-to-l from-[#E2F4FC] to-[#CBEFFF] opacity-70"
          aria-hidden="true"
        />
        <div
          className="absolute left-[15%] bottom-[5%] h-7 w-5 sm:h-9 sm:w-6 rounded-sm bg-primary-200 opacity-70"
          aria-hidden="true"
        />

        {/* Image-Badge Pairs */}
        {/* Camera Image with Music & Dances Badge */}
        <div
          className={`absolute bottom-[25%] ${showThirdPhoto ? 'left-[0%] md:bottom-[40%] w-[30%]' : 'left-[3%] md:bottom-[30%] w-[44%]'}`}
        >
          <div className="relative">
            <Image
              className="w-full rounded-md object-cover"
              src={cameraImg}
              alt="Camera"
              width={129}
              height={161}
              style={showThirdPhoto ? { height: 'auto', maxHeight: '161px' } : { height: 'auto' }}
              priority={true}
            />
            <Badge
              variant="subtle"
              className="absolute top-[-4px] md:top-auto md:bottom-[-4px] w-[130px] transform -translate-y-full md:translate-y-full left-0 z-30 font-medium text-[10px]"
              icon={<SvgMusicDance className="h-3 w-4 text-info-600" />}
            >
              {musicDancesLabel}
            </Badge>
          </div>
        </div>

        {/* Chess Image with Personal Development Badge */}
        <div className={`absolute top-[5%] ${showThirdPhoto ? 'left-[33%] w-[39%]' : 'right-[3%] w-[46%]'}`}>
          <div className="relative">
            <Badge
              variant="subtle"
              className="absolute bottom-[-4px] md:bottom-auto md:top-[-4px] transform translate-y-full md:-translate-y-full left-6 md:left-0 w-[170px] z-30 font-medium text-[10px]"
              icon={<SvgGrowth className="h-3 w-4 text-accent-rose-500" />}
            >
              {personalDevelopmentLabel}
            </Badge>
            {showThirdPhoto && (
              <Badge
                variant="subtle"
                className="absolute hidden md:inline-flex bottom-[-4px] left-3 w-[130px] transform translate-y-full z-30 font-medium text-[10px]"
                icon={<SvgHealthAndWelness className="h-3 w-4 text-accent-orange-400" />}
              >
                {healthWellnessLabel}
              </Badge>
            )}
            <Image
              className="w-full rounded-md object-cover"
              src={chessImg}
              alt="Chess"
              width={169}
              height={213}
              style={showThirdPhoto ? { height: 'auto', maxHeight: '213px' } : { height: 'auto' }}
              priority={true}
            />
          </div>
        </div>

        {/* Coffee Image */}
        {showThirdPhoto && (
          <div className="absolute right-[0%] top-[44%] w-[25%]">
            <div className="relative">
              <Image
                className="w-full rounded-md object-cover"
                src={cofeeImg}
                alt="Coffee"
                width={107}
                height={161}
                style={{
                  height: 'auto',
                  maxHeight: '161px'
                }}
                priority={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
